// MapLibre paints need resolved color values, not CSS custom properties. Keep those values in
// one semantic token object so white-label/theme changes have a single canvas-specific edit point.
export const MAP_THEME = {
  blankBackground: "#0b1018",
  checkpointStroke: "#0b1018",
  scanDot: "#93baff",
  dutyPingDot: "#5eead4",
  dutyPingStroke: "#0f766e",
  dutyPingStaleDot: "#f0b957",
  dutyPingStaleStroke: "#92400e",
  dutyPingOutsideDot: "#fb923c",
  dutyPingOutsideStroke: "#c2410c",
  /**
   * On-Call halo (lock Q1 B). A ring around the SAME person mark, never a second pin type.
   * Pearl white is the only strong hue left that no status layer owns: teal is on-shift, amber
   * is stale, orange is outside the boundary, rose is the temporary punch target. Temporary
   * until the Command marker sprites land in `@secops/icons`.
   */
  dutyOnCallHalo: "rgba(255, 255, 255, 0.14)",
  dutyOnCallHaloStroke: "rgba(255, 255, 255, 0.88)",
  /**
   * Free-duty cue (2026-08-20 amend, lock Q4 B). Same person glyph and the same fill, which
   * keeps carrying freshness and outside-boundary meaning; only the OUTLINE changes, because
   * "no post" is orthogonal to "how fresh" and must not overwrite it. Neutral slate is the hue
   * this map already uses for the Unassigned cluster, so one colour means one thing across the
   * desk: nobody assigned this. Never a second glyph family.
   */
  dutyFreeDutyStroke: "#cbd5e1",
  /**
   * Live site / area cluster bubbles. Teal keeps them in the duty presence family. The
   * Unassigned group gets the neutral slate treatment plus a heavier outline so it never reads
   * as a property: those pings have no site, and the map must not imply one. Temporary styling
   * until the cluster glyphs are extracted; do not hand-draw sprites for it.
   */
  dutyClusterFill: "rgba(94, 234, 212, 0.24)",
  dutyClusterStroke: "rgba(94, 234, 212, 0.9)",
  dutyClusterUnassignedFill: "rgba(148, 163, 184, 0.22)",
  dutyClusterUnassignedStroke: "rgba(203, 213, 225, 0.95)",
  dutyTrailCasing: "rgba(7, 11, 19, 0.92)",
  dutyTrailLine: "rgba(94, 234, 212, 0.55)",
  dutyTrailLineFocus: "rgba(94, 234, 212, 0.85)",
  /**
   * Identity colours for a site Replay tape. Session 2 lock: identity colours exclude red and
   * coral, because coral is SOS and red reads as an alarm. Twelve entries for the twelve-tape
   * cap, so the palette can never run out and start repeating an officer's colour.
   */
  replayIdentityTrail: [
    "rgba(94, 234, 212, 0.85)",
    "rgba(129, 191, 255, 0.85)",
    "rgba(196, 181, 253, 0.85)",
    "rgba(240, 189, 91, 0.85)",
    "rgba(134, 239, 172, 0.85)",
    "rgba(147, 197, 253, 0.85)",
    "rgba(216, 180, 254, 0.85)",
    "rgba(253, 224, 71, 0.85)",
    "rgba(45, 212, 191, 0.85)",
    "rgba(167, 243, 208, 0.85)",
    "rgba(186, 230, 253, 0.85)",
    "rgba(221, 214, 254, 0.85)",
  ] as readonly string[],
  geofenceFill: "rgba(106, 217, 144, 0.14)",
  geofenceStroke: "rgba(106, 217, 144, 0.72)",
  // L0 site property boundary — muted slate fill + stroke, sits below the checkpoint layers.
  siteBoundaryFill: "rgba(148, 163, 184, 0.10)",
  siteBoundaryStroke: "rgba(148, 163, 184, 0.55)",
  // L1 asset/building zones — muted indigo, above L0 site fill and below the checkpoint accents.
  siteAssetFill: "rgba(129, 140, 248, 0.12)",
  siteAssetStroke: "rgba(129, 140, 248, 0.60)",
  checkpointStatus: {
    active: "#6ad990",
    maintenance: "#f0b957",
    disabled: "#7c8aa3",
    default: "#93baff",
  },
  // PRD-001Y Wave C field-pin taxonomy. Three jobs for these values:
  //   1. Separate at property zoom on hybrid satellite, where mid-greens and mid-browns dominate.
  //   2. Never collide with live duty presence. dutyPingDot is teal and dutyPingOutsideDot is
  //      orange, so the draft accent stays violet: an operator glancing at CAD must never read a
  //      setup draft as a person. The brief calls this out explicitly.
  //   3. Read as a progression: routed is the settled green already used for active status,
  //      unrouted is sky (real, but not yet on a patrol), draft is violet (not real yet).
  fieldPin: {
    routedConfirmed: "#6ad990",
    unroutedConfirmed: "#38bdf8",
    draft: "#c084fc",
    // Drafts also get a violet ring instead of the shared white one, so the "not yet confirmed"
    // read survives even for a colour-blind operator who cannot separate the fills.
    draftStroke: "#7e22ce",
    defaultStroke: "#ffffff",
  },
  /**
   * Timeclock Show-on-map highlight (G3a). A TEMPORARY single point, so it must not be mistaken
   * for any standing layer: rose sits away from the field-pin progression (green/sky/violet),
   * away from live duty presence (teal/amber/orange), and away from the scan-dot blue. The halo
   * is the same hue at low alpha so the marker reads as one object, not a second pin.
   */
  punchHighlight: {
    dot: "#fb7185",
    stroke: "#ffffff",
    halo: "rgba(251, 113, 133, 0.22)",
    haloStroke: "rgba(251, 113, 133, 0.75)",
  },
} as const;
